// frontend/src/components/auth/register/AccountDataStep.tsx

import React from "react";
import {
  TextField,
  Box,
  Typography,
  InputAdornment,
  IconButton,
  Button,
  CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { FormikProps } from "formik";
import { PasswordStrengthIndicator } from "../../common/PasswordStrengthIndicator";
import { PasswordValidationResult } from "../../../services/passwordValidationService";
import { VERDE_MENTA } from "../../../themes/themeConstants";
import {
  registerFieldSx,
  registerEmailFieldSx,
  forgotPasswordButtonSx,
} from "./registerStyles";
import type { RegisterFormValues } from "../../../hooks/auth/useRegisterForm";

interface AccountDataStepProps {
  formik: FormikProps<RegisterFormValues>;
  t: {
    fullName: string;
    email: string;
    password: string;
    confirmPassword: string;
  };
  emailExists: boolean;
  checkingEmail: boolean;
  showPassword: boolean;
  setShowPassword: (v: boolean) => void;
  checkEmailExists: (email: string) => Promise<void>;
  setEmailExists: (v: boolean) => void;
  setCheckingEmail: (v: boolean) => void;
  handleForgotPassword: () => void;
  setPasswordValidation: (result: PasswordValidationResult | null) => void;
}

const AccountDataStep: React.FC<AccountDataStepProps> = ({
  formik,
  t,
  emailExists,
  checkingEmail,
  showPassword,
  setShowPassword,
  checkEmailExists,
  setEmailExists,
  setCheckingEmail,
  handleForgotPassword,
  setPasswordValidation,
}) => {
  return (
    <>
      <Typography
        variant="body2"
        sx={{ color: VERDE_MENTA, fontWeight: 500, mb: 0.5 }}
      >
        Nome Completo
      </Typography>
      <TextField
        fullWidth
        margin="none"
        id="fullName"
        name="fullName"
        placeholder={t.fullName}
        value={formik.values.fullName}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.fullName && Boolean(formik.errors.fullName)}
        helperText={formik.touched.fullName && formik.errors.fullName}
        sx={{ mb: 0.8, ...registerFieldSx }}
        InputProps={{ sx: { py: 0.3 } }}
      />

      <Typography
        variant="body2"
        sx={{ color: VERDE_MENTA, fontWeight: 500, mb: 0.5, mt: 1.5 }}
      >
        E-mail
      </Typography>
      <TextField
        fullWidth
        margin="none"
        id="email"
        name="email"
        placeholder={t.email}
        value={formik.values.email}
        onChange={(e) => {
          formik.handleChange(e);
          const email = e.target.value;
          if (!email || !email.includes("@")) {
            setEmailExists(false);
            setCheckingEmail(false);
          }
        }}
        onBlur={(e) => {
          formik.handleBlur(e);
          const email = e.target.value;
          if (email && email.includes("@")) {
            checkEmailExists(email);
          }
        }}
        error={formik.touched.email && (Boolean(formik.errors.email) || emailExists)}
        helperText={
          formik.touched.email && formik.errors.email
            ? formik.errors.email
            : emailExists
            ? "Este email já está em uso"
            : ""
        }
        sx={{
          mb: emailExists ? 0.5 : 1,
          ...registerEmailFieldSx(emailExists),
        }}
        InputProps={{
          sx: { py: 1.5 },
          endAdornment: checkingEmail ? (
            <InputAdornment position="end">
              <CircularProgress size={20} sx={{ color: VERDE_MENTA }} />
            </InputAdornment>
          ) : null,
        }}
      />

      {emailExists && (
        <Box sx={{ mb: 1, textAlign: "center" }}>
          <Button
            onClick={handleForgotPassword}
            variant="outlined"
            size="small"
            sx={forgotPasswordButtonSx}
          >
            Esqueci minha senha
          </Button>
        </Box>
      )}

      <Typography
        variant="body2"
        sx={{ color: VERDE_MENTA, fontWeight: 500, mb: 0.5, mt: 1.5 }}
      >
        Senha
      </Typography>
      <TextField
        fullWidth
        margin="none"
        id="password"
        name="password"
        placeholder={t.password}
        type={showPassword ? "text" : "password"}
        value={formik.values.password}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.password && Boolean(formik.errors.password)}
        helperText={formik.touched.password && formik.errors.password}
        sx={{ mb: 1.5, ...registerFieldSx }}
        InputProps={{
          sx: { py: 1.5 },
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
                sx={{ color: VERDE_MENTA }}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Box sx={{ mb: 0.3 }}>
        <PasswordStrengthIndicator
          password={formik.values.password}
          userContext={{
            name: formik.values.fullName,
            email: formik.values.email,
            firstName: formik.values.fullName?.split(" ")[0],
          }}
          showDetails={true}
          showRules={false}
          onValidation={(result) => setPasswordValidation(result)}
        />
      </Box>

      <Typography
        variant="body2"
        sx={{ color: VERDE_MENTA, fontWeight: 500, mb: 0.5, mt: 1 }}
      >
        Confirmar Senha
      </Typography>
      <TextField
        fullWidth
        margin="none"
        id="confirmPassword"
        name="confirmPassword"
        placeholder={t.confirmPassword}
        type={showPassword ? "text" : "password"}
        value={formik.values.confirmPassword}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={
          formik.touched.confirmPassword &&
          Boolean(formik.errors.confirmPassword)
        }
        helperText={
          formik.touched.confirmPassword && formik.errors.confirmPassword
        }
        sx={{ mb: 0.8, ...registerFieldSx }}
        InputProps={{ sx: { py: 0.3 } }}
      />
    </>
  );
};

export default AccountDataStep;
